import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  HStack,
  Heading,
  Image,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircleIcon, LockIcon } from '@chakra-ui/icons';
import { FaGoogle } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';

/**
 * /oauth/authorize — OAuth 2.1 consent page for the Origin Agent connector.
 *
 * ChatGPT (or any MCP client) sends the user here with the standard
 * authorization-code + PKCE query params. The page reuses the Hub's Google
 * SSO session, shows what is being granted, and on approve/deny POSTs the
 * decision (with the user's Firebase ID token) to /api/agent-oauth/decision,
 * which performs ALL server-side validation and returns the redirect URL
 * (always carrying `code`/`error`, `state`, and `iss` per RFC 9207).
 *
 * This page never builds the redirect itself and never trusts its own
 * parsing — display only. Public route; sign-in happens inline.
 */

// Human labels for the Origin capability scopes (mirror of
// functions/CRM/agentApi/constants.js SCOPES — display only).
const SCOPE_LABELS = {
  read: 'Read your contacts, deals, tasks, activity, and pipeline',
  write_deals: 'Update your deals — fields, stages, dispositions, and notes',
  write_contacts: 'Update your contacts — fields, tags, and notes',
  write_tasks: 'Create, update, and complete your tasks',
  create_leads: 'Create new leads owned by you',
  send_sms: 'Send SMS from your Dialpad number',
  send_email: 'Send email from your Gmail (and save drafts)',
  manage_campaigns: 'Enroll/unenroll contacts in your campaigns and pause them',
  admin_reports: 'Team activity reports and goals (works only for CRM admins)',
  // Elevated — SuperAdmins only; auto-granted server-side, never part of the
  // default display when the client requests no scopes.
  manage_agents: 'Manage the Hub\u2019s background AI agents (SuperAdmin)',
  gam_admin: 'Administer Google Workspace via GAM (allowlisted SuperAdmin only)',
};
const ELEVATED_SCOPE_KEYS = ['manage_agents', 'gam_admin'];
// Default display when the client requests no scopes — standard scopes only
// (mirrors the server's ALL_SCOPES default grant, which excludes elevated).
const DEFAULT_SCOPE_KEYS = Object.keys(SCOPE_LABELS).filter((k) => !ELEVATED_SCOPE_KEYS.includes(k));
const ALL_SCOPE_KEYS = Object.keys(SCOPE_LABELS);

function friendlyClientName(clientId, redirectUri) {
  const host = (() => {
    try {
      return new URL(redirectUri || clientId).hostname.toLowerCase();
    } catch {
      return '';
    }
  })();
  if (host.endsWith('chatgpt.com') || host.endsWith('openai.com')) return 'ChatGPT';
  if (host.endsWith('claude.ai') || host.endsWith('claude.com')) return 'Claude';
  return host || 'An AI assistant';
}

export default function AgentConnectorAuthorizePage() {
  const location = useLocation();
  const { currentUser, signInWithGoogle, logout } = useAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const scopeBoxBg = useColorModeValue('gray.50', 'gray.700');

  // 'review' | 'working' | 'redirecting' | 'blocked' | 'error'
  const [status, setStatus] = useState('review');
  const [message, setMessage] = useState(null);
  const [returnUrl, setReturnUrl] = useState(null);

  const params = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return {
      client_id: search.get('client_id') || '',
      redirect_uri: search.get('redirect_uri') || '',
      state: search.get('state') || '',
      scope: search.get('scope') || '',
      resource: search.get('resource') || '',
      response_type: search.get('response_type') || '',
      code_challenge: search.get('code_challenge') || '',
      code_challenge_method: search.get('code_challenge_method') || '',
    };
  }, [location.search]);

  const requestedScopes = useMemo(() => {
    const requested = params.scope.split(/\s+/).filter((s) => ALL_SCOPE_KEYS.includes(s));
    return requested.length ? requested : DEFAULT_SCOPE_KEYS;
  }, [params.scope]);

  const clientName = friendlyClientName(params.client_id, params.redirect_uri);
  const missingParams = !params.client_id || !params.redirect_uri || !params.code_challenge || params.response_type !== 'code';

  async function sendDecision(approve) {
    setStatus('working');
    setMessage(null);
    try {
      const fbUser = getAuth().currentUser;
      if (!fbUser) {
        setStatus('review');
        setMessage('Sign in first.');
        return;
      }
      const idToken = await fbUser.getIdToken();
      const res = await fetch('/api/agent-oauth/decision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, approve, params }),
      });
      const data = await res.json().catch(() => ({}));

      if (data.ok && data.redirectUrl) {
        setStatus('redirecting');
        window.location.assign(data.redirectUrl);
        return;
      }
      // Ineligible (or other structured refusal) — show the reason; offer the
      // safe validated return redirect when the server provided one.
      setStatus('blocked');
      setMessage(data.message || data.error || 'The request could not be completed.');
      setReturnUrl(data.redirectUrl || null);
    } catch (err) {
      console.error('Agent connector decision failed:', err);
      setStatus('error');
      setMessage('Could not reach Origin. Check your connection and try again.');
    }
  }

  async function handleSwitchAccount() {
    try {
      await logout();
    } catch (e) {
      console.error(e);
    }
  }

  let body;
  if (missingParams) {
    body = (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <VStack align="flex-start" spacing={1}>
          <Text fontWeight="semibold">Invalid authorization request</Text>
          <Text fontSize="sm">
            This link is missing required OAuth parameters (client_id, redirect_uri, response_type=code,
            PKCE code_challenge). Start the connection again from your AI assistant.
          </Text>
        </VStack>
      </Alert>
    );
  } else if (status === 'redirecting') {
    body = (
      <VStack spacing={4} py={6}>
        <Spinner size="lg" color="teal.500" thickness="3px" />
        <Text>Returning you to {clientName}…</Text>
      </VStack>
    );
  } else if (status === 'blocked' || status === 'error') {
    body = (
      <VStack spacing={4} align="stretch">
        <Alert status={status === 'blocked' ? 'warning' : 'error'} borderRadius="lg">
          <AlertIcon />
          <Text fontSize="sm">{message}</Text>
        </Alert>
        {status === 'error' && (
          <Button onClick={() => setStatus('review')} variant="outline">
            Try again
          </Button>
        )}
        {status === 'blocked' && returnUrl && (
          <Button onClick={() => window.location.assign(returnUrl)} colorScheme="teal" variant="outline">
            Return to {clientName}
          </Button>
        )}
      </VStack>
    );
  } else if (!currentUser) {
    body = (
      <VStack spacing={5}>
        <Text textAlign="center" color={subtleText}>
          Sign in with your Future Home Loans Google account to continue connecting {clientName} to Origin.
        </Text>
        <Button
          colorScheme="teal"
          leftIcon={<FaGoogle />}
          width="full"
          onClick={() => signInWithGoogle().catch((e) => console.error(e))}
        >
          Sign in with Google
        </Button>
      </VStack>
    );
  } else {
    body = (
      <VStack spacing={5} align="stretch">
        <HStack spacing={3}>
          <Avatar size="sm" src={currentUser.photoURL || undefined} name={currentUser.displayName || currentUser.email} />
          <Box>
            <Text fontWeight="medium" fontSize="sm">
              {currentUser.displayName || currentUser.email}
            </Text>
            <Text fontSize="xs" color={subtleText}>
              {currentUser.email} ·{' '}
              <Button variant="link" size="xs" colorScheme="teal" onClick={handleSwitchAccount}>
                Use a different account
              </Button>
            </Text>
          </Box>
        </HStack>

        <Box borderRadius="lg" bg={scopeBoxBg} p={4}>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>
            {clientName} will be able to act as you in Origin:
          </Text>
          <List spacing={1.5}>
            {requestedScopes.map((scope) => (
              <ListItem key={scope} fontSize="sm">
                <ListIcon as={CheckCircleIcon} color="teal.400" />
                {SCOPE_LABELS[scope]}
              </ListItem>
            ))}
          </List>
        </Box>

        <Text fontSize="xs" color={subtleText}>
          Every action is logged to the Origin audit trail and attributed to your account. An Origin admin can
          revoke this connection at any time.
        </Text>

        <HStack spacing={3}>
          <Button
            variant="outline"
            width="full"
            isDisabled={status === 'working'}
            onClick={() => sendDecision(false)}
          >
            Deny
          </Button>
          <Button
            colorScheme="teal"
            width="full"
            isLoading={status === 'working'}
            loadingText="Connecting"
            onClick={() => sendDecision(true)}
          >
            Allow
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <Box bg={pageBg} minH="100vh">
      <Center minH="100vh" px={4}>
        <Box bg={cardBg} borderRadius="xl" boxShadow="lg" p={8} w="full" maxW="md">
          <VStack spacing={4} align="stretch">
            <Center>
              <Image boxSize="90px" objectFit="contain" src={require('../assets/img/fhl-logo.png')} alt="Future Home Loans" />
            </Center>
            <VStack spacing={1}>
              <Heading size="md" textAlign="center">
                Connect {clientName} to Origin
              </Heading>
              <HStack>
                <LockIcon boxSize={3} color={subtleText} />
                <Badge colorScheme="teal" variant="subtle">
                  Secure OAuth connection
                </Badge>
              </HStack>
            </VStack>
            <Divider />
            {body}
          </VStack>
        </Box>
      </Center>
    </Box>
  );
}
